import classNames from 'classnames';
import React from 'react';
import { RCChip } from '../RCChip/RCChip';

interface RCSiteWrapperProps {
  /** Any/all content that should be wrapped */
  children: React.ReactNode;

  /** Narrow width here is only for a few components used in legacy one-column CMS pages, you should probably ignore it. */
  width?: 'default' | 'narrow';

  /** This will apply a pulse animation to the <b>main column only</b>; use if needed. */
  loading?: boolean;

  /** Use the provided `RCSiteWrapper.Sidebar` component set here – see the "with sidebar" story. */
  sidebar?: React.ReactNode;

  /** Sidebar state is handled externally. Use a `RCSiteWrapper.ToggleControl` to render a button that will hide & show when it's needed. */
  sidebarOpen?: boolean;

  /** You might end up needing to nest this inside a component that already contains wrapper logic. If so, set this to true to avoid doubling left & right gutters. */
  insideWrapper?: boolean;
}

/**
 * This is our main site wrapper. Oftentimes you will find yourself using it with no props set just to provide a width & gutter for your content.
 *
 * However, it also supports a sidebar that becomes toggleable on tablet & narrower widths; see the examples & comments below for how to take advantage of this.
 */
export function RCSiteWrapper({
  children,
  width = 'default',
  loading = false,
  sidebarOpen = false,
  insideWrapper = false,
  sidebar,
}: RCSiteWrapperProps) {
  return (
    <div className={classNames(
      'rc-site-wrapper',
      {
        'rc-site-wrapper--narrow': width === 'narrow',
        'rc-site-wrapper--loading': loading,
        'rc-site-wrapper--with-sidebar': !!sidebar,
        'rc-site-wrapper--inside-wrapper': insideWrapper,
        'rc-site-wrapper--sidebar-open': sidebarOpen,
      },
    )}>
      {sidebar ?
        <>
          {sidebar}
          <div className="rc-site-wrapper__main-column">
            {children}
          </div>
        </>
        : children
      }
    </div>
  );
}

interface SiteWrapperToggleControlProps {
  onChange: () => void;
  children: React.ReactNode;
}

function SiteWrapperToggleControl({
  onChange,
  children,
}: SiteWrapperToggleControlProps) {
  return (
    <div className="rc-site-wrapper__toggle-control">
      <RCChip
        onClick={onChange}
      >
        {children}
      </RCChip>
    </div>
  );
}

interface SiteWrapperSidebarContentProps {
  children: React.ReactNode;
}

function SiteWrapperSidebarContent({
  children,
}: SiteWrapperSidebarContentProps) {
  return (
    <div className="rc-site-wrapper__sidebar__content">
      {children}
    </div>
  );
}

interface SiteWrapperSidebarProps {
  children: React.ReactNode;
}

function SiteWrapperSidebar({
  children,
}: SiteWrapperSidebarProps) {
  return (
    <div className="rc-site-wrapper__sidebar">
      {children}
    </div>
  );
}

interface SiteWrapperSidebarHeaderProps {
  children: React.ReactNode;
}

function SiteWrapperSidebarHeader({
  children,
}: SiteWrapperSidebarHeaderProps) {
  return (
    <div className="rc-site-wrapper__sidebar__header">
      {children}
    </div>
  );
}

interface SiteWrapperSidebarFooterProps {
  children: React.ReactNode;
}

function SiteWrapperSidebarFooter({
  children,
}: SiteWrapperSidebarFooterProps) {
  return (
    <div className="rc-site-wrapper__sidebar__footer">
      {children}
    </div>
  );
}

RCSiteWrapper.ToggleControl = SiteWrapperToggleControl;
RCSiteWrapper.Sidebar = SiteWrapperSidebar;
RCSiteWrapper.SidebarContent = SiteWrapperSidebarContent;
RCSiteWrapper.SidebarHeader = SiteWrapperSidebarHeader;
RCSiteWrapper.SidebarFooter = SiteWrapperSidebarFooter;
